import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, takeNext } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleButton, OrDivider } from "@/components/AuthCallback";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success("Account created");
      const { resumePendingPlan } = await import("@/components/Pricing");
      if (await resumePendingPlan()) return;
      nav(takeNext());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex items-center justify-center bg-muted border-r border-border paper-noise">
        <div className="max-w-md px-10">
          <div className="font-mono-label text-muted-foreground mb-6">Start free · No card required</div>
          <h1 className="font-serif-display text-5xl tracking-tighter leading-none mb-6">
            Read smarter.<br />Starting with<br /><span className="italic text-primary">the next file.</span>
          </h1>
          <p className="text-muted-foreground">Upload your first PDF in under a minute. Citations included.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <Link to="/" className="inline-flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-foreground text-background grid place-items-center font-serif-display text-lg font-black">D</div>
            <span className="font-serif-display text-xl">DocuMind</span>
          </Link>
          <div>
            <h2 className="font-serif-display text-3xl mb-2">Create your account</h2>
            <p className="text-sm text-muted-foreground">Already registered? <Link to="/login" className="underline">Log in</Link></p>
          </div>
          <GoogleButton label="Sign up with Google" />
          <OrDivider />
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="font-mono-label">Name</Label>
              <Input id="name" data-testid="signup-name" required value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="email" className="font-mono-label">Email</Label>
              <Input id="email" data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono-label">Password</Label>
              <Input id="password" data-testid="signup-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
          </div>
          <Button type="submit" data-testid="signup-submit" disabled={loading} className="w-full h-11 bg-foreground text-background hover:bg-primary rounded-sm btn-invert">
            {loading ? "Creating…" : "Create account →"}
          </Button>
          <p className="text-xs text-muted-foreground">By continuing you agree to our terms and privacy policy.</p>
        </form>
      </div>
    </div>
  );
}
